import compact from 'lodash/compact';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  ChargeMasterDTO,
  ChargeMasterDTOPaginatedResponseDTO,
  NPISearchResponseDTO,
  GetApiChargeSearchCustomChargeMasterParams,
  CustomChargeMasterDTO,
  BulkPatientStatementDTO,
  BulkPaperClaimsDTO,
  ChargeFilterDTO,
  PatientPaymentTransactionViewModelDTO,
  GetApiChargeSearchChargeMasterParams,
  GetApiClaimSearchExternalProviderParams,
  EOBFilterDTO,
  ChargesRequestDTO,
  ClaimsRequestDTO,
} from 'dtos';
import {
  ChargeSortingOption,
} from 'dtos/chargeSortingOption';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';
import {
  BillingDataParams,
  PatientPaymentTransaction,
  PaymentDataParams,
} from 'pages/Dashboard/pages/Billing/types';
import {
  compactPatient,
} from 'pages/Dashboard/utils/helper';
import {
  getApiChargeSearchCustomChargeMaster,
  getApiClaimSearchExternalProvider,
  postApiBillingGetBulkPatientStatements,
  postApiBillingGetEOBs,
  postApiClaimGetBulkPaperClaims,
  postApiBillingGetPatientPaymentTransactions,
  getApiChargeSearchChargeMaster,
} from 'endpoints';
import {
  PaginationState,
} from '@tanstack/react-table';
import {
  GetEOBsRequestDTO,
} from 'dtos/getEOBsRequestDTO';
import {
  EOBSummaryResponseDTO,
} from 'dtos/eOBSummaryResponseDTO';
import isNil from 'lodash/isNil';
import {
  sendRequest,
} from 'utils/httpClient';
import {
  ProviderSearchParams,
} from 'pages/Dashboard/types/provider';

export type ChargesFilter = ChargeFilterDTO & {
  PayerIdList: number[];
  ClaimTagList: number[];
};

// @TODO: dtoName is wrong letter case and makes complete mess with a model
export function normalizeChargesQuery({
  PayerIdList: payerIdList,
  ClaimTagList: claimTagList,
  ...filter
}: ChargesFilter): ChargeFilterDTO {
  return {
    ...filter,
    PayerIdList: map(payerIdList ?? [], 'payerEntryId'),
    ClaimTagList: map(claimTagList ?? [], 'tagId'),
  } as ChargeFilterDTO;
}

type BillingRequestParams = {
  dateRange?: DateRange;
  searchQuery?: string;
  filterQuery?: ChargesFilter;
  pagination?: PaginationState;
};

function buildBillingRequest({
  dateRange,
  searchQuery,
  filterQuery,
  pagination,
}: BillingRequestParams) {
  return {
    dateRange: serializeDateRange(dateRange),
    searchText: searchQuery,
    filterQuery: filterQuery ? normalizeChargesQuery(filterQuery) : undefined,
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
  };
}

export function buildChargesRequest(params: BillingRequestParams): ChargesRequestDTO {
  return buildBillingRequest(params);
}

export function buildClaimsRequest(params: BillingRequestParams): ClaimsRequestDTO {
  return buildBillingRequest(params);
}

export const getBillingCharges = async (
  params: BillingDataParams,
): Promise<ChargeMasterDTOPaginatedResponseDTO> => {
  const response = await getApiChargeSearchChargeMaster({
    searchComponents: params.searchQuery,
    sortBy: ChargeSortingOption.Default,
    page: params.pagination?.pageIndex ?? 0,
    pageSize: params.pagination?.pageSize ?? 25,
  } satisfies GetApiChargeSearchChargeMasterParams);

  return response;
};

// adopted for table data call
export const getBillingChargesData = async (
  params: BillingDataParams,
): Promise<ChargeMasterDTO[]> => {
  const response = await getBillingCharges(params);

  params.setTotalSize(response.pageProperties?.totalSize ?? 0);

  return [...(response.data ?? [])];
};

export const getCustomServicesData = async ({
  searchQuery,
  pagination,
  setTotalSize,
}: BillingDataParams): Promise<CustomChargeMasterDTO[]> => {
  const response = await getApiChargeSearchCustomChargeMaster({
    searchComponents: searchQuery,
    sortBy: ChargeSortingOption.Default,
    page: pagination?.pageIndex ?? 0,
    pageSize: pagination?.pageSize ?? 25,
  } satisfies GetApiChargeSearchCustomChargeMasterParams);

  setTotalSize(response.pageProperties?.totalSize ?? 0);

  return [...(response.data ?? [])];
};

export const requestPatientPaymentsData = async ({
  pagination,
  dateRange,
  filterQuery,
  patientSearchText,
  setTotalSize,
  requestMedia,
}: PaymentDataParams): Promise<PatientPaymentTransaction[]> => {
  const response = await postApiBillingGetPatientPaymentTransactions({
    dateRange: serializeDateRange(dateRange),
    patientSearchText,
    filterQuery,
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
  });

  setTotalSize(response?.pageProperties?.totalSize ?? 0);

  const patients = response?.data?.map(
    (payment) => payment?.patient ?? {},
  ) ?? [];

  const result = (response?.data ?? [])
    .map((item: PatientPaymentTransactionViewModelDTO) => ({
      ...item,
      patient: {
        ...compactPatient(item.patient ?? {}),
        otherContact: item.patient?.otherContact,
      },
      isRefund: !isNil(item.patientPaymentRefundId),
    })) as PatientPaymentTransaction[];

  if (isFunction(requestMedia)) {
    const patientIds = compact(map(patients, 'patientId'));
    requestMedia(patientIds, MediaOriginatorType.Patient);

    const userIds = compact(result.map((item) => item.createdByUser?.userId));
    requestMedia(userIds, MediaOriginatorType.User);
  }

  return result;
};

export const getEOBsData = async ({
  pagination,
  dateRange,
  searchText,
  filterQuery,
  setTotalSize,
  requestMedia,
}: PaymentDataParams): Promise<EOBSummaryResponseDTO[]> => {
  const {
    PatientIdList: patientIdList,
    ...restFilterQuery
  } = (filterQuery ?? {}) as Record<string, any>;

  // @TODO: dtoName is wrong letter case and makes complete mess with a model
  const normalizedFilterQuery = {
    ...restFilterQuery,
    PatientIdList: map(patientIdList ?? [], 'patientId'),
  } as EOBFilterDTO;

  const payload: GetEOBsRequestDTO = {
    dateRange: serializeDateRange(dateRange),
    searchText,
    filter: normalizedFilterQuery,
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
  };

  const response = await postApiBillingGetEOBs(payload);

  setTotalSize(response?.pageProperties?.totalSize ?? 0);

  const data = response.data ?? [];

  if (isFunction(requestMedia)) {
    const userIds = compact(
      data.flatMap((eob) => [eob.createdByUserId, eob.reviewedByUserId]),
    );
    requestMedia(userIds, MediaOriginatorType.User);
  }

  return [...data];
};

export const getProvidersByParams = (
  params: ProviderSearchParams,
): Promise<NPISearchResponseDTO> =>
  // casting is safe, becuase ProviderSearchParams shadows GetApiClaimSearchExternalProviderParams
  getApiClaimSearchExternalProvider(params as GetApiClaimSearchExternalProviderParams);

export function getLookupOptions(
  optionLookupUrl: string,
  search: string,
  page?: number,
  pageSize?: number,
) {
  return sendRequest<any[]>({
    url: `api${optionLookupUrl}`,
    method: 'GET',
    params: { search, page, pageSize },
  });
}

type StatementsDataParams = {
  dateRange?: DateRange;
  pagination?: PaginationState;
  setTotalSize?: (size: number) => void;
  requestMedia?: (ids: number[], originatorType?: MediaOriginatorType) => void;
};

export async function getStatementList({
  dateRange,
  pagination,
  setTotalSize,
  requestMedia,
}: StatementsDataParams): Promise<BulkPatientStatementDTO[]> {
  const { reportResults } = await postApiBillingGetBulkPatientStatements({
    dateRange: serializeDateRange(dateRange),
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
  });

  setTotalSize?.(reportResults?.pageProperties?.totalSize ?? 0);

  const data = reportResults?.data ?? [];

  if (isFunction(requestMedia)) {
    const userIds = compact(data.map((item) => item.requestedByUserDTO?.userId));
    requestMedia(userIds, MediaOriginatorType.User);
  }

  return [...data];
}

export async function getBulkClaims({
  dateRange,
  pagination,
  setTotalSize,
}: StatementsDataParams): Promise<BulkPaperClaimsDTO[]> {
  const { reportResults } = await postApiClaimGetBulkPaperClaims({
    dateRange: serializeDateRange(dateRange),
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
  });

  setTotalSize?.(reportResults?.pageProperties?.totalSize ?? 0);

  return [...(reportResults?.data ?? [])];
}
